/**
 * InputAutocomplete is a component that provides an autocomplete input field.
 * It allows users to select multiple options from a dropdown list.
 *
 * Props:
 * - value: The current value of the autocomplete input, which can be a string, array of strings, or array of IOptions.
 * - options: An array of selectable options, each represented as an object with a 'label' and 'value'.
 * - label: A string to display as the input field's label.
 * - required: Boolean to indicate if the input is required.
 * - labelColor: Color of the label text.
 * - helperText: Additional text to provide guidance or context to the user.
 * - error: Boolean to indicate if there's an error in the input.
 * - onChange: Callback function to handle changes to the input value.
 * - placeholder: Placeholder text to display when the input is empty.
 * - multiple: Boolean to allow selection of multiple options.
 * - disableClearable: Boolean to disable the clear button.
 * - showChip: Determines where the selected options are displayed as chips, either "inside" or "outside".
 */
import {
  Autocomplete,
  AutocompleteProps,
  FormHelperText,
  Stack,
  SxProps,
  TextField,
  Theme,
  useTheme
} from "@mui/material"
import React from "react"
import Conditional from "../conditional"
import Label from "../label"
import ChipStyled from "../chipStyled"
interface Label {
  value: string
  label: string
  [key: string]: any
}

type IInputAutocompleteProps = {
  // value: string | string[] | IOptions[]
  // options: Label[]
  label?: string
  required?: boolean
  labelColor?: any
  helperText?: string | string[]
  error?: boolean
  onChange: (value: IOptions[]) => void
  placeholder?: string
  multiple?: boolean
  showChip?: "inside" | "outside" | "hidden"
  maxWidth?: string
  labelMb?: number
  height?: string
  getOptionLabel?: (option: any) => string
  sx?: SxProps<Theme>
  slotProps?: any
  isOptionValueSame?: boolean
  // valueKey?: string
  startAdornment?: React.ReactNode
} & Omit<AutocompleteProps<any, any, any, any>, "renderInput">

const InputAutocomplete = React.forwardRef(
  (
    {
      value = "",
      // options,
      label,
      required,
      labelColor,
      helperText,
      error,
      onChange,
      placeholder,
      multiple,
      showChip,
      maxWidth,
      isOptionValueSame,
      height,
      labelMb,
      getOptionLabel,
      getOptionKey,
      // valueKey = "value",
      sx,
      startAdornment,
      ...rest
    }: IInputAutocompleteProps,
    ref?: any
  ) => {
    if (label === "Branches (locations)") console.log("value=inputautocomplete", { value, label })
    const theme = useTheme()
    const handleisOptionEqualToValue = (option: any, value: any) => {
      return getOptionKey?.(option) === getOptionKey?.(value)
    }
    const inputPalette = (theme.palette as any).input as
      | { labelColor?: string; placeholderColor?: string; boxBgColor?: string }
      | undefined
    return (
      <Stack sx={{ width: "100%", ...(maxWidth && { maxWidth }) }}>
        <Label
          mb={labelMb}
          label={label}
          labelColor={error ? "error" : (labelColor ?? inputPalette?.labelColor)}
          required={required}
        />
        <Autocomplete
          multiple={multiple}
          sx={{ "& .MuiInputBase-root ": { height }, ...sx }}
          size="small"
          // options={options}
          value={(value ?? (multiple ? [] : "")) as any}
          onChange={(_, newValue) => {
            onChange?.(newValue as any)
          }}
          ref={ref}
          filterSelectedOptions
          getOptionKey={getOptionKey}
          getOptionLabel={getOptionLabel}
          {...(["outside", "hidden"].includes(showChip ?? "") && { renderTags: () => <></> })}
          isOptionEqualToValue={isOptionValueSame ? handleisOptionEqualToValue : undefined}
          renderInput={(params) => (
            <TextField
              {...params}
              autoComplete={"off"}
              placeholder={placeholder}
              variant="outlined"
              error={error}
              slotProps={{
                input: {
                  ...params.InputProps,
                  ...((!showChip && startAdornment) || (showChip === "outside" && startAdornment)
                    ? {
                        startAdornment: startAdornment
                      }
                    : {}),
                  sx: {
                    "&::placeholder": {
                      color: inputPalette?.placeholderColor,
                      opacity: 1
                    }
                  }
                }
              }}
            />
          )}
          {...rest}
        />

        <Conditional
          condition={Boolean(multiple && showChip === "outside" && typeof value === "object" && value?.length !== 0)}
        >
          <Stack direction={"row"} flexWrap={"wrap"} gap={2} my={2}>
            {value?.length ? (
              (value as IOptions[])?.map((val) => (
                <ChipStyled
                  size="small"
                  label={getOptionLabel?.(val) ?? val.value}
                  onDelete={() =>
                    onChange?.(
                      (value as IOptions[])?.filter(
                        (item) => item?.value !== val?.value || getOptionKey?.(val) !== getOptionKey?.(item)
                      )
                    )
                  }
                />
              ))
            ) : (
              <></>
            )}
          </Stack>
        </Conditional>
        {/* </FormControl> */}
        {helperText ? (
          <FormHelperText sx={{ fontSize: (theme) => theme.typography.subtitle2.fontSize }} error={error}>
            {helperText}
          </FormHelperText>
        ) : (
          <></>
        )}
      </Stack>
    )
  }
)
export default InputAutocomplete
